import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { ReportHead, Empty } from '../components/Shell.jsx';

const SUBTYPES = {
  asset: ['current_asset', 'non_current_asset'],
  liability: ['current_liability', 'non_current_liability'],
  equity: ['equity'],
  income: ['revenue', 'other_income'],
  expense: ['cost_of_sales', 'operating_expense', 'finance_cost', 'tax_expense'],
};
const LABEL = {
  current_asset: 'Current asset', non_current_asset: 'Non-current asset',
  current_liability: 'Current liability', non_current_liability: 'Non-current liability',
  equity: 'Equity', revenue: 'Revenue', other_income: 'Other income',
  cost_of_sales: 'Cost of sales', operating_expense: 'Operating expense',
  finance_cost: 'Finance cost', tax_expense: 'Taxation',
};

/**
 * Whether an account may be declared a cash book — the same rule as migration
 * 051's account_is_bank_is_postable_balance_sheet CHECK, which is the one that
 * actually decides. A bank account is something you hold money in or owe money
 * on: an asset (cheque account, petty cash, trust account) or a liability (a
 * credit card). Never income or expense, and never a heading.
 */
const isBankable = (a) => a.is_postable && (a.type === 'asset' || a.type === 'liability');

export function ChartOfAccounts({ entity }) {
  const [accounts, setAccounts] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'expense', subtype: 'operating_expense' });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/entities/${entity.id}/accounts`)
    .then((d) => setAccounts(d.accounts)).catch((e) => setErr(e.message));
  useEffect(() => { if (entity?.id) load(); }, [entity?.id]);

  const add = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/api/entities/${entity.id}/accounts`, form);
      setMsg(`Account ${form.code} added.`);
      setForm({ code: '', name: '', type: 'expense', subtype: 'operating_expense' });
      setAdding(false); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const save = async (id, patch) => {
    setErr(null); setMsg(null);
    try { await api.patch(`/api/entities/${entity.id}/accounts/${id}`, patch); setEditing(null); load(); }
    catch (e) { setErr(e.message); }
  };

  const remove = async (id, code) => {
    setErr(null); setMsg(null);
    try { await api.del(`/api/entities/${entity.id}/accounts/${id}`); setMsg(`Account ${code} deleted.`); load(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <>
      <ReportHead entity={entity} title="Chart of accounts"
                  sub="Every account this client's books can post to." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="note">
        Once an account carries a posting, its <strong>code, type and subtype are frozen</strong> —
        changing them would move a balance between the income statement and the balance sheet and
        silently rewrite every prior comparative. Rename and deactivate stay available.
      </div>

      <div className="note">
        Mark every account you keep a <strong>cash book</strong> for as a bank account — a cheque
        account, petty cash, a trust account, a credit card. That is the list a cash book batch can
        be opened against, and there is no limit on how many you have. Marking stays available after
        an account has been posted to. An account with batches already captured against it cannot
        stop being a bank account.
      </div>

      {adding ? (
        <div style={{ margin: '18px 0' }}>
          <div className="form-grid">
            <label className="field"><span>Code</span>
              <input className="input--figure" value={form.code} placeholder="6425"
                     onChange={(e) => setForm({ ...form, code: e.target.value })} /></label>
            <label className="field"><span>Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="field"><span>Type</span>
              <select value={form.type} onChange={(e) => setForm({
                ...form, type: e.target.value, subtype: SUBTYPES[e.target.value][0] })}>
                {Object.keys(SUBTYPES).map((t) =>
                  <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
              </select></label>
            <label className="field"><span>Appears under</span>
              <select value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })}>
                {SUBTYPES[form.type].map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
              </select></label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" onClick={add} disabled={busy || !form.code || form.name.length < 2}>
              {busy ? 'Adding' : 'Add account'}
            </button>
            <button className="btn btn--quiet" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" style={{ margin: '18px 0' }} onClick={() => setAdding(true)}>
          Add an account
        </button>
      )}

      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr><th style={{ width: 70 }}>Code</th><th>Account</th><th>Appears under</th>
                <th>Control</th><th style={{ width: 200 }} /></tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} style={{ opacity: a.is_active ? 1 : 0.5 }}>
                <td className="code figure">{a.code}</td>
                <td>
                  {editing === a.id ? (
                    <input defaultValue={a.name} style={{ width: '100%' }}
                           onKeyDown={(e) => { if (e.key === 'Enter') save(a.id, { name: e.target.value }); }}
                           id={`n-${a.id}`} />
                  ) : (
                    <>
                      {a.name}
                      {!a.is_postable && <span style={{ color: 'var(--ink-faint)' }}> · heading</span>}
                      {!a.is_active && <span className="neg"> · inactive</span>}
                    </>
                  )}
                </td>
                <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{LABEL[a.subtype]}</td>
                <td style={{ fontSize: 12, color: 'var(--debit)' }}>
                  {a.control_kind ? a.control_kind.replace(/_/g, ' ') : ''}
                  {a.is_bank && (
                    <span style={{ color: 'var(--credit)' }}>
                      {a.control_kind ? ' · ' : ''}cash book
                    </span>
                  )}
                </td>
                <td className="num">
                  {editing === a.id ? (
                    <>
                      <button className="icon-btn"
                              onClick={() => save(a.id, { name: document.getElementById(`n-${a.id}`).value })}>
                        Save
                      </button>{' '}
                      <button className="icon-btn" onClick={() => setEditing(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="icon-btn" onClick={() => setEditing(a.id)}>Rename</button>{' '}
                      <button className="icon-btn" onClick={() => save(a.id, { isActive: !a.is_active })}>
                        {a.is_active ? 'Deactivate' : 'Restore'}
                      </button>{' '}
                      {/* Offered only where the database would accept it — a cash book is a
                          postable asset or liability (petty cash, a trust account, a credit
                          card). Showing the button on Sales would be showing a button whose
                          only outcome is a 422. */}
                      {isBankable(a) && (
                        <>
                          <button className="icon-btn" onClick={() => save(a.id, { isBank: !a.is_bank })}>
                            {a.is_bank ? 'Not a bank' : 'Mark as bank'}
                          </button>{' '}
                        </>
                      )}
                      {!a.control_kind && (
                        <button className="icon-btn" onClick={() => remove(a.id, a.code)}>Delete</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
